(() => {
  const ROOT_SELECTOR = '[data-gst-navigation]';
  const OPEN_CLASS = 'is-open';
  const hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
  let cartDrawerInstance = null;

  const initializeCartDrawer = () => {
    if (cartDrawerInstance) return;
    if (document.body.classList.contains('template-cart')) return;
    if (!document.querySelector('#CartDrawer')) return;
    if (window.theme?.settings?.cartType !== 'drawer') return;
    if (typeof window.theme?.CartDrawer !== 'function') return;

    cartDrawerInstance = new window.theme.CartDrawer();
  };

  class GstNavigation {
    constructor(root) {
      if (root.dataset.gstNavigationReady === 'true') return;

      this.root = root;
      this.groups = Array.from(root.querySelectorAll('[data-gst-nav-group]'));
      this.mobile = root.querySelector('[data-gst-mobile-nav]');
      this.closeTimers = new WeakMap();
      root.dataset.gstNavigationReady = 'true';
      document.documentElement.classList.add('js-gst-nav-ready');

      this.groups.forEach((group) => this.bindDesktopGroup(group));
      this.bindHoverBoundary();
      this.bindMobileMenu();
      this.bindCartDrawerState();
      this.bindGlobalControls();
    }

    bindDesktopGroup(group) {
      const trigger = group.querySelector(':scope > button[aria-controls]');
      const menu = trigger ? document.getElementById(trigger.getAttribute('aria-controls')) : null;
      const closeButton = group.querySelector('[data-gst-nav-close]');

      if (!trigger || !menu) return;

      trigger.addEventListener('click', () => {
        if (group.classList.contains('is-pinned')) {
          this.close(group);
        } else {
          this.open(group, { pinned: true });
        }
      });

      trigger.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
        event.preventDefault();
        this.open(group, { pinned: true });
        const links = Array.from(menu.querySelectorAll('a[href]'));
        const target = event.key === 'ArrowDown' ? links[0] : links[links.length - 1];
        window.requestAnimationFrame(() => target?.focus());
      });

      group.addEventListener('pointerenter', () => {
        if (!hoverQuery.matches) return;
        this.clearCloseTimer(group);
        this.open(group);
      });

      group.addEventListener('focusin', () => {
        this.clearCloseTimer(group);
      });

      group.addEventListener('focusout', (event) => {
        if (event.relatedTarget && group.contains(event.relatedTarget)) return;
        this.closeTimers.set(group, window.setTimeout(() => {
          if (!group.contains(document.activeElement)) this.close(group);
        }, 0));
      });

      closeButton?.addEventListener('click', () => {
        this.close(group);
        trigger.focus();
      });

      menu.querySelectorAll('a[href]').forEach((link) => {
        link.addEventListener('click', () => this.closeAll());
      });
    }

    bindHoverBoundary() {
      this.root.addEventListener('pointerenter', () => {
        if (!hoverQuery.matches) return;
        this.groups.forEach((group) => this.clearCloseTimer(group));
      });

      this.root.addEventListener('pointerleave', () => {
        if (!hoverQuery.matches) return;
        this.groups.forEach((group) => {
          if (!group.classList.contains(OPEN_CLASS) || group.classList.contains('is-pinned')) return;
          this.closeTimers.set(group, window.setTimeout(() => this.close(group), 420));
        });
      });
    }

    bindMobileMenu() {
      if (!this.mobile) return;

      const summary = this.mobile.querySelector(':scope > summary');
      const childDisclosures = Array.from(this.mobile.querySelectorAll('.gst-mobile-nav__panel > details'));

      this.mobile.addEventListener('toggle', () => {
        const isOpen = this.mobile.open;
        summary?.setAttribute('aria-label', isOpen ? 'Close navigation menu' : 'Open navigation menu');
        document.body.classList.toggle('gst-mobile-nav-open', isOpen);
        if (!isOpen) childDisclosures.forEach((details) => details.removeAttribute('open'));
      });

      childDisclosures.forEach((details) => {
        details.addEventListener('toggle', () => {
          if (!details.open) return;
          childDisclosures.forEach((other) => {
            if (other !== details) other.removeAttribute('open');
          });
        });
      });

      this.mobile.querySelectorAll('a[href]').forEach((link) => {
        link.addEventListener('click', () => {
          this.mobile.removeAttribute('open');
          document.body.classList.remove('gst-mobile-nav-open');
        });
      });
    }

    bindCartDrawerState() {
      const trigger = this.root.querySelector('[data-gst-cart-trigger]');
      const drawer = document.querySelector('#CartDrawer');

      if (!trigger || !drawer) return;

      const syncState = () => {
        const isOpen = drawer.classList.contains('drawer--is-open');
        trigger.setAttribute('aria-expanded', String(isOpen));
        trigger.setAttribute('aria-label', isOpen ? 'Close cart' : 'Open cart');
      };

      syncState();
      new MutationObserver(syncState).observe(drawer, {
        attributes: true,
        attributeFilter: ['class', 'aria-hidden']
      });
    }

    bindGlobalControls() {
      this.root.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;

        const openGroup = this.groups.find((group) => group.classList.contains(OPEN_CLASS));
        if (openGroup) {
          const trigger = openGroup.querySelector(':scope > button');
          this.close(openGroup);
          trigger?.focus();
        }

        if (this.mobile?.open) {
          this.mobile.removeAttribute('open');
          document.body.classList.remove('gst-mobile-nav-open');
          this.mobile.querySelector(':scope > summary')?.focus();
        }
      });

      document.addEventListener('pointerdown', (event) => {
        if (!this.root.contains(event.target)) {
          this.closeAll();
          if (this.mobile?.open) this.mobile.removeAttribute('open');
          document.body.classList.remove('gst-mobile-nav-open');
        }
      });
    }

    open(group, { pinned = false } = {}) {
      this.closeAll(group);
      this.clearCloseTimer(group);
      group.classList.add(OPEN_CLASS);
      if (pinned) group.classList.add('is-pinned');
      group.querySelector(':scope > button')?.setAttribute('aria-expanded', 'true');
    }

    close(group) {
      this.clearCloseTimer(group);
      group.classList.remove(OPEN_CLASS, 'is-pinned');
      group.querySelector(':scope > button')?.setAttribute('aria-expanded', 'false');
    }

    closeAll(exception) {
      this.groups.forEach((group) => {
        if (group !== exception) this.close(group);
      });
    }

    clearCloseTimer(group) {
      const timer = this.closeTimers.get(group);
      if (timer) window.clearTimeout(timer);
      this.closeTimers.delete(group);
    }
  }

  const initialize = (scope = document) => {
    const roots = [];
    if (scope.matches?.(ROOT_SELECTOR)) roots.push(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach((root) => roots.push(root));
    roots.forEach((root) => new GstNavigation(root));
    initializeCartDrawer();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize());
  } else {
    initialize();
  }

  window.addEventListener('load', initializeCartDrawer, { once: true });
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
